/**
 * TeachingVideoPlayer — athlete-facing teaching video viewer.
 *
 * Displayed on the template detail page when template.teaching_video_id is set.
 * Supports three source types:
 *   - 'video': renders <video> element + sidebar transcript
 *   - 'audio': renders <audio> element + full-width transcript
 *   - 'paste': no media; renders formatted transcript only
 *
 * Audio mode toggle (Product Decision §2): switches to black background with
 * high-contrast white transcript text for accessibility / audio-only consumption.
 */

import { useRef, useState } from 'react';
import { MoonIcon, SunIcon } from '@heroicons/react/24/outline';
import { useTeachingVideoTranscript } from '@/hooks/useTeachingVideo';
import { useTeachingMediaUrl } from '@/hooks/useMediaUrl';
import { useStableMediaSrc } from '@/hooks/useStableMediaSrc';
import { Spinner } from '@/components/ui';

interface TeachingVideoPlayerProps {
  /** TeachingVideo ID linked to this template. */
  teachingVideoId: string;
  /** Source type from the TeachingVideo record. */
  sourceType: 'video' | 'audio' | 'paste';
}

export default function TeachingVideoPlayer({
  teachingVideoId,
  sourceType,
}: TeachingVideoPlayerProps) {
  const [audioMode, setAudioMode] = useState(false);

  const { data: transcriptData, isLoading } = useTeachingVideoTranscript(teachingVideoId);
  // Media-auth security sprint (2026-09-21): /teaching-videos/{id}/stream now
  // requires a bearer token or a signed sig/exp pair — a bare <video src> /
  // <audio src> sends neither. streamUrl is null while the media-token fetch
  // is in flight; the <video>/<audio> elements below are gated on it.
  const { streamUrl } = useTeachingMediaUrl(teachingVideoId);

  // Keep playback position stable across a signed-URL refresh. <video> and
  // <audio> need distinct ref TYPES (React's JSX ref prop is element-type
  // specific), but only one of the two ever actually mounts (sourceType is
  // mutually exclusive) — the other hook call simply no-ops on a null ref.
  // See backlog/media-url-refresh-playback-position.md.
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  useStableMediaSrc(videoRef, streamUrl);
  useStableMediaSrc(audioRef, streamUrl);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Spinner size="md" label="Loading coach instruction..." />
      </div>
    );
  }

  const transcript = transcriptData?.transcript ?? null;
  const movement = transcriptData?.movement ?? null;
  const sport = transcriptData?.sport ?? null;

  // Audio mode: black background + large transcript text
  if (audioMode) {
    return (
      <div className="rounded-lg overflow-hidden" style={{ backgroundColor: '#000' }}>
        {/* Controls bar */}
        <div
          className="flex items-center justify-between px-4 py-2"
          style={{ backgroundColor: '#111' }}
        >
          <span className="text-xs text-gray-400 font-medium">
            {movement ?? 'Coach Instruction'}
            {sport ? ` — ${sport}` : ''}
          </span>
          <button
            type="button"
            onClick={() => setAudioMode(false)}
            className="flex items-center gap-1 text-xs text-gray-400 hover:text-white transition-colors"
          >
            <SunIcon className="h-4 w-4" />
            Standard view
          </button>
        </div>

        {/* Audio player — gated on streamUrl (signed); never emits the
            unsigned URL, which would 401 (media-auth security sprint). */}
        {sourceType !== 'paste' && (
          <div className="px-4 py-3" style={{ backgroundColor: '#111' }}>
            {streamUrl ? (
              <audio
                ref={audioRef}
                controls
                src={streamUrl}
                className="w-full"
                style={{ colorScheme: 'dark' }}
              >
                Your browser does not support the audio element.
              </audio>
            ) : (
              <p style={{ color: '#666', fontSize: '0.75rem' }}>Loading audio…</p>
            )}
          </div>
        )}

        {/* Transcript — large white text on black */}
        <div className="px-6 py-8">
          {transcript ? (
            <p
              className="leading-relaxed whitespace-pre-wrap"
              style={{ color: '#ffffff', fontSize: '1.125rem', lineHeight: '1.75' }}
            >
              {transcript}
            </p>
          ) : (
            <p style={{ color: '#666', fontStyle: 'italic' }}>
              No transcript available.
            </p>
          )}
        </div>
      </div>
    );
  }

  // Standard mode
  return (
    <div className="rounded-lg border border-border-subtle bg-dark-card overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle">
        <div>
          <p className="text-sm font-semibold text-text-primary">
            {movement ?? 'Coach Instruction'}
          </p>
          {sport && (
            <p className="text-xs text-text-muted">{sport}</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => setAudioMode(true)}
          className="flex items-center gap-1 text-xs text-text-muted hover:text-text-primary transition-colors"
          title="Switch to audio-only mode with large transcript"
        >
          <MoonIcon className="h-4 w-4" />
          Audio mode
        </button>
      </div>

      {/* Media + transcript layout */}
      <div
        className={`flex ${sourceType === 'video' ? 'flex-col lg:flex-row' : 'flex-col'}`}
      >
        {/* Video player — gated on streamUrl (signed); never emits the
            unsigned URL, which would 401 (media-auth security sprint). */}
        {sourceType === 'video' && (
          <div className="lg:flex-1 bg-black flex items-center justify-center min-h-[200px]">
            {streamUrl ? (
              <video
                ref={videoRef}
                controls
                src={streamUrl}
                className="max-h-[400px] w-full object-contain"
              >
                Your browser does not support the video element.
              </video>
            ) : (
              <p className="text-sm text-text-muted">Loading video…</p>
            )}
          </div>
        )}

        {/* Audio player */}
        {sourceType === 'audio' && (
          <div className="p-4 bg-dark-elevated-2 border-b border-border-subtle">
            {streamUrl ? (
              <audio ref={audioRef} controls src={streamUrl} className="w-full">
                Your browser does not support the audio element.
              </audio>
            ) : (
              <p className="text-sm text-text-muted">Loading audio…</p>
            )}
          </div>
        )}

        {/* Transcript */}
        <div
          className={`p-4 overflow-y-auto ${sourceType === 'video' ? 'lg:w-72 lg:border-l lg:border-border-subtle' : ''}`}
          style={{ maxHeight: sourceType === 'video' ? '400px' : 'none' }}
        >
          <p className="text-xs font-medium text-text-muted mb-2 uppercase tracking-wide">
            Transcript
          </p>
          {transcript ? (
            <p className="text-sm text-text-secondary leading-relaxed whitespace-pre-wrap">
              {transcript}
            </p>
          ) : (
            <p className="text-sm text-text-muted italic">
              {sourceType === 'paste'
                ? 'No coaching notes available.'
                : 'Transcript not yet available.'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
